import * as api from '../utils/api';
import { parseAnnuairePath } from './workshopBookingReschedule';

/**
 * FIXERLIGHT-049 — cross-repo contract with
 * `docs/specs/fixerlight-049-ecrire-au-reparateur.md` (repo api). Client
 * side only (`/clients/{client}/message-threads*`), same doctrine as
 * `services/workshopBookings.js` (FIXERLIGHT-052) : `ClientAccess` +
 * `checkObjectAccess()` server-side, same `api.get`/`api.post`.
 */
export const MESSAGE_THREADS_PATH = (clientId) =>
  `/clients/${clientId}/message-threads`;
export const MESSAGE_THREAD_PATH = (clientId, threadId) =>
  `${MESSAGE_THREADS_PATH(clientId)}/${threadId}`;
export const MESSAGE_THREAD_REPLY_PATH = (clientId, threadId) =>
  `${MESSAGE_THREAD_PATH(clientId, threadId)}/reply`;
export const WORKSHOP_BOOKING_MESSAGES_PATH = (clientId, bookingId) =>
  `/clients/${clientId}/workshop-bookings/${bookingId}/messages`;

// Mirrors `MessageThreadService::MAX_MESSAGE_LENGTH` (api, contrat § 2,
// Points ouverts n° 3) — no endpoint exposes it, the contract fixes the
// number.
export const MAX_MESSAGE_LENGTH = 2000;

/**
 * `api.post`/`api.get` (`src/utils/api.jsx`) resolve the parsed body
 * regardless of HTTP status and never surface the status code itself. The
 * api's `Handler.php` gives this shape on `/v1`:
 *   - validation failure (`Validator::validate()`/`Request::validate()`) →
 *     `{ error_message, errors: {field: [...]} }`, HTTP 400 — despite the
 *     contract's own wording ("422"), the actual code path always renders
 *     400 for a `ValidationException` (verified in `Handler.php`).
 *   - a fil clos refusé (`clientReply`) → `CFException(422, "Ce fil est
 *     clos...")`, rendered `{ error_message, errors: [] }` — no machine
 *     `code` (unlike `WorkshopBookingActionError`'s `booking_too_close`
 *     etc., `MessageThreadService` never sets one on this contract). The
 *     front never hits this branch in normal use (see
 *     `MessageDetail.jsx`, which reopens instead of replying on a closed
 *     fil) but is defended here regardless.
 *   - rate limit (`throttle:v1-message-threads`) → `{ error_message: "Too
 *     Many Attempts." }`, HTTP 429.
 *   - not found (shop/thread hors tenant) → `{ error_message: "... not
 *     found." }`, HTTP 404.
 * `kind` below is a best-effort classification of these shapes, since no
 * status code and no `code` field are available to discriminate them
 * precisely.
 */
export class MessageThreadActionError extends Error {
  constructor(kind, message) {
    super(message || kind || 'message-thread-action-error');
    this.name = 'MessageThreadActionError';
    this.kind = kind || 'generic';
  }
}

function classifyErrorResponse(response) {
  const errors = response && response.errors;
  if (
    errors &&
    typeof errors === 'object' &&
    !Array.isArray(errors) &&
    Object.keys(errors).length
  ) {
    return Object.prototype.hasOwnProperty.call(errors, 'booking_id')
      ? 'booking_invalid'
      : 'validation';
  }
  const message = (response && response.error_message) || '';
  if (/too many/i.test(message)) return 'rate_limited';
  if (/clos/i.test(message)) return 'thread_closed';
  if (/not found/i.test(message)) return 'not_found';
  return 'generic';
}

function throwIfActionError(response, kindOverrides = {}) {
  if (response && (response.error_message || response.errors)) {
    const kind = classifyErrorResponse(response);
    throw new MessageThreadActionError(
      kindOverrides[kind] || kind,
      response.error_message,
    );
  }
}

/**
 * @param {number|string} clientId
 * @returns {Promise<Array>} the threads, in the exact order the api sent
 * them (`last_message_at DESC`, contrat § 2) — never re-sort.
 */
export async function getMessageThreads(clientId) {
  const response = await api.get(MESSAGE_THREADS_PATH(clientId));
  if (!response || !Array.isArray(response.data)) {
    throw new Error('message-threads: unexpected response');
  }
  return response.data;
}

/**
 * @param {number|string} clientId
 * @param {number|string} threadId
 * @returns {Promise<Object>} the thread with its messages.
 * @throws on transport failure, a 404 (thread not found / not this
 * client's), or any other unexpected shape — callers show one generic
 * error, same doctrine as `getWorkshopBooking` (no attempt to distinguish
 * 404 from a network failure on this GET).
 */
export async function getMessageThread(clientId, threadId) {
  const response = await api.get(MESSAGE_THREAD_PATH(clientId, threadId));
  if (!response || !response.data) {
    throw new Error('message-threads: unexpected response');
  }
  return response.data;
}

/**
 * Opens a new fil, or continues/reopens the one that already exists for
 * this (client, shop) — contrat § 1/§ 2, `openOrContinue`.
 * @param {number|string} clientId
 * @param {{shopId: number, body: string, origin: string, originContext?: Object}} params
 * @returns {Promise<Object>} the thread (with its messages).
 * @throws {MessageThreadActionError}
 */
export async function openMessageThread(
  clientId,
  { shopId, body, origin, originContext },
) {
  const payload = { shop_id: shopId, body, origin };
  if (originContext) payload.origin_context = originContext;
  const response = await api.post(MESSAGE_THREADS_PATH(clientId), payload);
  throwIfActionError(response);
  if (!response || !response.data) {
    throw new MessageThreadActionError(
      'generic',
      'message-threads: unexpected response',
    );
  }
  return response.data;
}

/**
 * Replies inside an OPEN fil — refused on a closed one (contrat § 2) : use
 * {@link openMessageThread} to reopen instead.
 * @param {number|string} clientId
 * @param {number|string} threadId
 * @param {string} body
 * @param {?number} [bookingId] FIXERLIGHT-300 — the rendez-vous the message
 * is about; `null` = explicitly none, `undefined` = key not sent (fil
 * without any rendez-vous to choose from).
 * @returns {Promise<Object>} the thread (with its messages).
 * @throws {MessageThreadActionError}
 */
export async function replyMessageThread(clientId, threadId, body, bookingId) {
  const payload = { body };
  if (bookingId !== undefined) payload.booking_id = bookingId;
  const response = await api.post(
    MESSAGE_THREAD_REPLY_PATH(clientId, threadId),
    payload,
  );
  throwIfActionError(response);
  if (!response || !response.data) {
    throw new MessageThreadActionError(
      'generic',
      'message-threads: unexpected response',
    );
  }
  return response.data;
}

/**
 * FIXERLIGHT-300 — writes to the workshop from a rendez-vous: opens,
 * continues or reopens the cyclist/workshop fil and attaches the message to
 * that rendez-vous (api#1212). Same resource as {@link openMessageThread}.
 * @param {number|string} clientId
 * @param {number|string} bookingId
 * @param {string} body
 * @returns {Promise<Object>} the thread (with its messages).
 * @throws {MessageThreadActionError}
 */
export async function sendWorkshopBookingMessage(clientId, bookingId, body) {
  const response = await api.post(
    WORKSHOP_BOOKING_MESSAGES_PATH(clientId, bookingId),
    { body },
  );
  // 404 here = the rendez-vous isn't the cyclist's, or the workshop is
  // suspended — not a missing fil.
  throwIfActionError(response, { not_found: 'booking_not_found' });
  if (!response || !response.data) {
    throw new MessageThreadActionError(
      'generic',
      'message-threads: unexpected response',
    );
  }
  return response.data;
}

/**
 * `ClientMessageThreadResource` never gives a numeric shop id, only
 * `shop.url` (contrat § 4) — same gap `workshopBookingReschedule.js`
 * already works around from `booking.shop.url`. Needed to reopen a closed
 * fil from the detail screen (`POST .../message-threads` wants `shop_id`).
 * @param {?string} shopUrl `thread.shop.url` — `null` when the fiche isn't
 * published.
 * @returns {Promise<?number>} `null` when the fiche is absent or the
 * resolution fails (api en panne, fiche dépubliée depuis).
 */
export async function resolveShopIdFromUrl(shopUrl) {
  const path = parseAnnuairePath(shopUrl);
  if (!path) return null;
  try {
    const shopItem = await api.get(
      `/shops/by-path/${path.dept}/${path.city}/${path.slug}`,
    );
    return (shopItem && shopItem.id) || null;
  } catch (e) {
    return null;
  }
}
